import { addMonths, endOfMonth, format, startOfMonth } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { collection, doc, getDocsFromServer, query, Timestamp, where } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import firebaseFirestore from '~/config/firebase/firestore';
import { Location } from '~/core/types';
import { aMinutos, normalizarHora, ReservaOcupacion, SalaInforme } from './ocupacion';

/**
 * Los datos del informe: las salas con hora de inicio y fin, y las reservas
 * del rango, **traídas mes a mes y guardadas en memoria**.
 *
 * Por qué se consulta directo y no se guarda un resumen en la nube (como la
 * libreta del buscador de clientes): se midió en producción el 18/09/2026 y
 * NOI tiene ~250 reservas al mes, de ~2,2 KB cada una. Un mes son ~250
 * lecturas y ~0,5 MB, que llegan en un segundo; un año entero, ~3.000
 * lecturas (menos de un centavo). Un resumen precalculado ahorraría eso a
 * cambio de otra función en la nube, otra reconstrucción y otra cosa que
 * puede quedar desfasada. No compensa.
 *
 * Lo que sí se hace es no volver a pedir lo ya pedido: cada mes se guarda en
 * memoria mientras el informe está abierto, así que cambiar de horas, días o
 * salas es instantáneo, y ampliar el rango solo trae los meses que faltan.
 * **Al abrir el informe se vuelve a pedir todo**: un check-out que se registra
 * hoy sobre una reserva del mes pasado tiene que verse sin recargar la página.
 */

const ZONA = 'America/Bogota';

type MesCargado = { reservas: ReservaOcupacion[]; ilegibles: ReservaIlegible[] };

// Caché por empresa y mes ('yyyy-MM'), fuera de React. Se vacía en cada
// apertura del informe (ver `useDatosOcupacion`).
const cacheMeses = new Map<string, MesCargado>();

const claveMes = (companyId: string, mes: Date) => `${companyId}|${format(mes, 'yyyy-MM')}`;

/** Reservas que no se pudieron contar por tener horas ilegibles (fin antes que inicio, etc.). */
export type ReservaIlegible = { id: string; code: string; locationId: string; startHour: string; endHour: string; dia: Date | null; estado: string };

/** Una reserva de Firestore convertida a lo que el cálculo necesita; null si no sirve. */
export const aReservaOcupacion = (id: string, r: Record<string, any>): ReservaOcupacion | null => {
  const marca: Timestamp | undefined = r.startDatetime ?? r.datetime;
  if (!marca?.toDate) return null;
  const local = utcToZonedTime(marca.toDate(), ZONA);
  const dia = new Date(local.getFullYear(), local.getMonth(), local.getDate());
  const inicio = aMinutos(normalizarHora(r.startHour) ?? normalizarHora(r.time) ?? '');
  const fin = aMinutos(normalizarHora(r.endHour) ?? '');
  if (inicio === null || fin === null || fin <= inicio) return null;
  return {
    id,
    code: String(r.code ?? ''),
    nombre: String(r.namesAndSurnames ?? r.name ?? ''),
    locationId: String(r.location?.id ?? ''),
    dia,
    inicio,
    fin,
    estado: String(r.status ?? 'pending'),
    personas: Math.max(0, Number(r.numberPeople ?? 0) || 0),
    monto: Math.max(0, Number(r.payment?.amount ?? 0) || 0),
  };
};

const pedirMes = async (companyId: string, mes: Date): Promise<MesCargado> => {
  const desde = startOfMonth(mes);
  const hasta = endOfMonth(mes);
  // Del servidor, nunca de la caché local: sin conexión, `getDocs` resuelve
  // con lo que haya en caché (a menudo nada) sin avisar, y el informe habría
  // enseñado un 0 % con cara de dato. Así, sin conexión, falla y se ve el
  // aviso con "Reintentar".
  const snap = await getDocsFromServer(
    query(
      collection(firebaseFirestore, 'reservations'),
      where('location.company', '==', doc(firebaseFirestore, 'companies', companyId)),
      where('datetime', '>=', Timestamp.fromDate(desde)),
      where('datetime', '<=', Timestamp.fromDate(hasta))
    )
  );
  const reservas: ReservaOcupacion[] = [];
  const ilegibles: ReservaIlegible[] = [];
  snap.forEach((d) => {
    const datos = d.data();
    const r = aReservaOcupacion(d.id, datos);
    if (r) {
      reservas.push(r);
      return;
    }
    // Solo interesa avisar de las que tienen hora de fin y no se cancelaron:
    // una zona de día completo o una visita no tienen hora de fin y no van
    // en este informe de todos modos.
    if (datos.endHour && datos.endHour !== '--' && datos.status !== 'cancelled') {
      const marca: Timestamp | undefined = datos.startDatetime ?? datos.datetime;
      ilegibles.push({
        id: d.id,
        code: String(datos.code ?? ''),
        locationId: String(datos.location?.id ?? ''),
        startHour: String(datos.startHour ?? ''),
        endHour: String(datos.endHour ?? ''),
        dia: marca?.toDate ? utcToZonedTime(marca.toDate(), ZONA) : null,
        estado: String(datos.status ?? 'pending'),
      });
    }
  });
  return { reservas, ilegibles };
};

/** Los meses que toca un rango, del primero al último. */
export const mesesDelRango = (desde: Date, hasta: Date): Date[] => {
  const lista: Date[] = [];
  for (let m = startOfMonth(desde); m.getTime() <= hasta.getTime(); m = addMonths(m, 1)) lista.push(m);
  return lista;
};

export const useDatosOcupacion = (companyId: string | undefined, desde: Date, hasta: Date, activo: boolean) => {
  const [salas, setSalas] = useState<SalaInforme[]>([]);
  const [cargandoSalas, setCargandoSalas] = useState(true);
  const [reservas, setReservas] = useState<ReservaOcupacion[]>([]);
  const [ilegibles, setIlegibles] = useState<ReservaIlegible[]>([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [intento, setIntento] = useState(0);

  // Cada apertura empieza con la caché vacía: lo que cambió desde la última
  // vez (un check-out de ayer, una cancelación) tiene que verse.
  useEffect(() => {
    if (activo) cacheMeses.clear();
  }, [activo]);

  // Salas con hora de inicio y fin, activas o no (las inactivas se ofrecen en el filtro).
  useEffect(() => {
    if (!companyId || !activo) return;
    let vivo = true;
    (async () => {
      try {
        setCargandoSalas(true);
        const snap = await getDocsFromServer(
          query(
            collection(firebaseFirestore, 'locations'),
            where('company', '==', doc(firebaseFirestore, 'companies', companyId))
          )
        );
        if (!vivo) return;
        const lista: SalaInforme[] = snap.docs
          .map((d) => {
            const datos = d.data() as Location & { schedule?: any; status?: string };
            const ajustes = datos.schedule?.settings ?? {};
            if (!ajustes.isEndDateEnable || ajustes.isReservationWholeDay) return null;
            return {
              id: d.id,
              nombre: datos.name ?? d.id,
              activa: (datos.status ?? 'active') === 'active',
              capacidad: Number(ajustes.maximumCapacity ?? 0),
              semana: datos.schedule?.['weekly-dates'] ?? {},
              precioHora: Number(ajustes.hourlyPrice ?? 0) > 0 ? Number(ajustes.hourlyPrice) : null,
              descuento: Math.min(1, Math.max(0, Number(ajustes.multiHourDiscountPercent ?? 0) / 100)) || 0,
            } as SalaInforme;
          })
          .filter((s): s is SalaInforme => s !== null)
          .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
        setSalas(lista);
      } catch (e) {
        if (vivo) setError('No se pudieron cargar las salas.');
      } finally {
        if (vivo) setCargandoSalas(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [companyId, activo]);

  const desdeMs = desde.getTime();
  const hastaMs = hasta.getTime();

  useEffect(() => {
    if (!companyId || !activo) return;
    let vivo = true;
    (async () => {
      setError(null);
      const meses = mesesDelRango(new Date(desdeMs), new Date(hastaMs));
      const faltan = meses.filter((m) => !cacheMeses.has(claveMes(companyId, m)));
      if (faltan.length) setCargando(true);
      try {
        await Promise.all(
          faltan.map(async (m) => {
            cacheMeses.set(claveMes(companyId, m), await pedirMes(companyId, m));
          })
        );
        if (!vivo) return;
        const cargados = meses.map((m) => cacheMeses.get(claveMes(companyId, m)));
        setReservas(cargados.flatMap((c) => c?.reservas ?? []));
        setIlegibles(cargados.flatMap((c) => c?.ilegibles ?? []));
      } catch (e) {
        if (!vivo) return;
        setError('No se pudieron cargar las reservas. Revisa la conexión y vuelve a intentar.');
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [companyId, activo, desdeMs, hastaMs, intento]);

  const reintentar = () => {
    cacheMeses.clear();
    setIntento((n) => n + 1);
  };
  /** Tras guardar una tarifa desde el simulador: el informe la usa sin recargar. */
  const actualizarTarifas = (cambios: { id: string; precioHora: number; descuento: number }[]) => {
    setSalas((lista) =>
      lista.map((s) => {
        const c = cambios.find((x) => x.id === s.id);
        return c ? { ...s, precioHora: c.precioHora > 0 ? c.precioHora : null, descuento: c.descuento } : s;
      })
    );
  };
  const salasActivas = useMemo(() => salas.filter((s) => s.activa), [salas]);

  return { salas, salasActivas, cargandoSalas, reservas, ilegibles, cargando, error, reintentar, actualizarTarifas };
};
